import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const REFERENCES = [
  {
    name: "SalesTec Solutions",
    category: "Website Modernisierung",
    url: "https://www.salestec-solutions.de/",
    year: "2026",
  },
];

export const References = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".ref-header > *",
        { y: reduced ? 0 : 32, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: { trigger: ".ref-header", start: "top 82%" },
        }
      );
      gsap.fromTo(
        ".ref-line",
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1.1,
          ease: "power3.out",
          scrollTrigger: { trigger: ".ref-list", start: "top 80%" },
        }
      );
      gsap.fromTo(
        ".ref-row",
        { y: reduced ? 0 : 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.15,
          scrollTrigger: { trigger: ".ref-list", start: "top 78%" },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="referenzen"
      data-section-idx="4"
      className="bg-greige relative overflow-hidden py-28 lg:py-40"
      data-testid="references-section"
    >
      <div className="mesh-overlay" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="ref-header max-w-3xl">
          <p className="eyebrow mb-6 text-[#6E685F]">005 / 006 — Referenz</p>
          <h2 className="font-display text-4xl text-[#2B2825] sm:text-5xl lg:text-6xl">
            Aktuelles Projekt
          </h2>
          <p className="mt-6 max-w-xl text-base text-[#6E685F] md:text-lg">
            Eine Auswahl unserer jüngsten Arbeiten — handverlesen.
          </p>
        </div>

        <div className="ref-list mx-auto mt-16 max-w-5xl lg:mt-24">
          <div className="ref-line h-px w-full origin-left bg-[#2B2825]/15" />
          {REFERENCES.map((ref, i) => (
            <a
              key={ref.name}
              href={ref.url}
              target="_blank"
              rel="noreferrer"
              className="ref-row group block no-underline"
              data-testid={`reference-card-${i}`}
            >
              <div className="ref-row-inner relative flex flex-col gap-3 py-10 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:py-12 lg:py-16">
                <div className="flex items-baseline gap-6 sm:gap-10">
                  <span className="eyebrow text-[#6E685F]/70 hidden sm:inline">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="ref-name font-display text-3xl text-[#2B2825] sm:text-5xl lg:text-6xl">
                      {ref.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#6E685F] sm:text-base">
                      {ref.category}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <span className="hidden text-sm text-[#6E685F]/80 sm:inline">
                    {ref.year}
                  </span>
                  <span
                    className="ref-arrow flex h-12 w-12 items-center justify-center rounded-full border border-[#2B2825]/15 text-[#2B2825] transition-all duration-500 group-hover:border-[#2B2825] group-hover:bg-[#2B2825] group-hover:text-[#F5F1E8]"
                    data-testid={`reference-link-${i}`}
                  >
                    <ArrowUpRight size={20} strokeWidth={1.75} />
                  </span>
                </div>

                <span className="ref-row-bar absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-[#2B2825]/40 transition-transform duration-700 ease-out group-hover:scale-x-100" />
              </div>
              <div className="h-px w-full bg-[#2B2825]/15" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
